// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient } from '../services/api-client.js';
import { useLocale } from '../hooks/useLocale.js';
import { usePageTitle } from '../hooks/usePageTitle.js';
import { useAnnounce } from '../hooks/useAnnounce.js';
import { useAuth } from '../contexts/AuthContext.js';
import { safeNextPath } from '../utils/safe-next-path.js';
import { isMfaCodeShape, normaliseMfaCode } from '../utils/mfa-code.js';

/**
 * Sprint 63.6.3, gate E12.5 pass 2 — the code prompt for a session that EXISTS
 * and is held at the TOTP step-up (403 `MFA_REQUIRED`).
 *
 * ## Why this page exists
 *
 * A session can be held without the person having just typed a password:
 *
 *  - a single-sign-on sign-in by somebody who holds an authenticator, where the
 *    organisation does not accept the provider's own factor (or the provider's
 *    claims did not match what it accepts);
 *  - a signed-in session whose provider-factor trust an administrator narrowed
 *    or withdrew afterwards — those sessions are returned to the platform's own
 *    code prompt, which is what the help articles say happens.
 *
 * The only place the SPA could take a code was the password form's second step
 * (`LoginPage`), which needs the password. A federated person has none, and a
 * live session has no login form to be returned to, so the promised "platform's
 * own code prompt" did not exist: every page failed with a generic error and
 * the only exit was signing out.
 *
 * ## What it calls, and nothing else
 *
 * `POST /mfa/verify` (the same call the login form's second step makes — the
 * server also accepts a backup code there, so this accepts one too), then
 * `GET /auth/me` to rebuild the UI's view of the session, then it continues to
 * `?next=` (a same-origin path only, validated exactly as `LoginPage` does) or
 * `/admin`. `POST /auth/logout` is the way out, offered in every state. All
 * three are on the list a held session is admitted to, which is why this page
 * sits outside `ProtectedRoute` (the wizard read it makes is not).
 */
export function MfaStepUpPage() {
  const { t } = useLocale();
  usePageTitle(t.login.mfa_step_up_title);
  const navigate = useNavigate();
  const { logout, refreshAuth, isAuthenticated, loading } = useAuth();
  const { announce } = useAnnounce();
  const [searchParams] = useSearchParams();
  const continueTo = safeNextPath(searchParams.get('next')) ?? '/admin';

  /*
   * No session at all (it expired, or this address was opened signed out): there
   * is no code that could be entered, so the person goes to the sign-in page,
   * which brings them back here if their sign-in is held. A WRONG CODE does not
   * end up here: the api-client does not treat a 401 `INVALID_CODE` as a dead
   * session.
   */
  useEffect(() => {
    if (loading || isAuthenticated) return;
    const rawNext = searchParams.get('next');
    navigate(
      safeNextPath(rawNext)
        ? `/login?next=${encodeURIComponent(safeNextPath(rawNext) as string)}`
        : '/login',
      { replace: true },
    );
  }, [loading, isAuthenticated, navigate, searchParams]);

  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // fix-sprint-20261012-1200 F4 — the code was accepted but `/auth/me` did not
  // answer: the session is verified on the server and the UI does not know it.
  const [refreshFailed, setRefreshFailed] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  /*
   * fix-sprint-20261012-1200 F4 — the input is disabled while the request runs,
   * which drops focus to `body`; a wrong code must leave the person in the
   * field to type again. Restored once the field is enabled.
   */
  useEffect(() => {
    if (!busy && error) inputRef.current?.focus();
  }, [busy, error]);

  const normalised = normaliseMfaCode(code);
  const plausible = isMfaCodeShape(normalised);

  function messageFor(err: unknown): string {
    const serverCode = (err as { serverCode?: string } | null)?.serverCode;
    const status = (err as { status?: number } | null)?.status;
    if (serverCode === 'MFA_VERIFICATION_LOCKED' || status === 423) {
      return t.login.mfa_step_up_locked;
    }
    if (serverCode === 'MFA_NOT_ENROLLED') return t.login.mfa_step_up_not_enrolled;
    if (serverCode === 'INVALID_CODE' || serverCode === 'VALIDATION_ERROR' || status === 400) {
      return t.login.invalid_mfa;
    }
    return t.common.operation_failed;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (busy || !plausible) return;
    setBusy(true);
    setError('');
    try {
      await apiClient.verifyMfa({ code: normalised });
    } catch (err: unknown) {
      // fix-sprint-20261012-1200 F2 — no authenticator left and the
      // organisation requires one: the server moved the session to the
      // enrolment hold, so the person goes to set one up.
      if ((err as { serverCode?: string } | null)?.serverCode === 'MFA_ENROLMENT_REQUIRED') {
        await refreshAuth();
        navigate('/mfa-enrolment-required', { replace: true });
        return;
      }
      // One announcement: the error paragraph is `role="alert"` (F4).
      setError(messageFor(err));
      setBusy(false);
      return;
    }
    // The code was accepted (or, with no authenticator left under a policy
    // that does not require one, the prompt was lifted): the server's session
    // is verified. Rebuild the UI's view of it and carry on.
    announce(t.login.mfa_step_up_accepted);
    await continueAfterVerification();
  }

  /**
   * fix-sprint-20261012-1200 F4 — `refreshAuth()` answers `false` for a
   * failure that is not a 401. Navigating then sent the person to a route that
   * bounced straight back here with no message, after their single-use code had
   * been spent. Say so, and offer the reload again instead.
   */
  async function continueAfterVerification() {
    setBusy(true);
    const ok = await refreshAuth();
    if (!ok) {
      setRefreshFailed(true);
      setBusy(false);
      return;
    }
    setRefreshFailed(false);
    navigate(continueTo, { replace: true });
  }

  return (
    <div className="dm-page">
      <div className="dm-header">
        <h1>{t.login.mfa_step_up_title}</h1>
        <p>{t.login.mfa_step_up_intro}</p>
      </div>

      {error && (
        <p className="dm-alert dm-alert--danger" role="alert" id="mfa-step-up-error">
          {error}
        </p>
      )}

      {refreshFailed ? (
        <div
          className="dm-alert dm-alert--danger"
          role="alert"
          data-testid="mfa-step-up-refresh-failed"
        >
          <p>{t.login.mfa_step_up_refresh_failed}</p>
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy}
            onClick={() => void continueAfterVerification()}
          >
            {t.common.retry}
          </button>
        </div>
      ) : (
        <form className="dm-form" onSubmit={handleSubmit} aria-label={t.login.mfa_step_up_title}>
          <div className="dm-form-field">
            <label htmlFor="mfa-step-up-code">{t.login.mfa_step_up_code_label}</label>
            <input
              id="mfa-step-up-code"
              type="text"
              inputMode="text"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder={t.login.mfa_step_up_placeholder}
              required
              aria-required="true"
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? 'mfa-step-up-hint mfa-step-up-error' : 'mfa-step-up-hint'}
              disabled={busy}
              ref={inputRef}
              autoFocus
            />
            <small id="mfa-step-up-hint">{t.login.mfa_step_up_instruction}</small>
          </div>
          <button type="submit" className="btn btn-primary" disabled={busy || !plausible}>
            {busy ? t.login.verifying : t.login.verify}
          </button>
        </form>
      )}

      {/*
       * Always available, in every state: a person who cannot proceed must be
       * able to leave, and `POST /api/v1/auth/logout` is on the list a held
       * session is admitted to.
       */}
      <button
        type="button"
        className="dm-badge-danger"
        onClick={() => {
          logout();
          navigate('/login');
        }}
      >
        {t.login.mfa_enrolment_required_sign_out}
      </button>
    </div>
  );
}

export default MfaStepUpPage;
